<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import useAuth from '@/composables/useAuth';
import { useSelectedWarehouse } from '@/composables';
import { useArrivalsNotificationSource } from '@/composables/notifications/useArrivalsNotificationSource';
import { useNotificationCenter } from '@/composables/notifications/useNotificationCenter';

const { t } = useI18n();
const { meRef } = useAuth();
const { selectedWarehouse } = useSelectedWarehouse();

const arrivalsSource = useArrivalsNotificationSource({
  warehouse: selectedWarehouse,
  userId: () => meRef.value?.id
});
const notificationCenter = useNotificationCenter([arrivalsSource]);

const { attentionCount, hasAttention, hasUnseen } = notificationCenter;

const label = computed(() =>
  t('navigation.notifications.arrivalsNeedingAttention', attentionCount.value)
);

const bellState = computed(() => {
  if (hasUnseen.value) {
    return 'unseen';
  }
  if (hasAttention.value) {
    return 'attention';
  }

  return 'none';
});
</script>

<template>
  <v-tooltip location="bottom" :text="t('navigation.notifications.arrivals')">
    <template #activator="{ props: tooltipProps }">
      <v-btn
        v-bind="tooltipProps"
        icon
        variant="text"
        :aria-label="label"
        :data-state="bellState"
        data-testid="top-nav-notifications"
        @click="notificationCenter.toggle()">
        <v-badge
          :model-value="bellState !== 'none'"
          dot
          color="warning"
          :class="{ 'top-nav-notifications-unseen': bellState === 'unseen' }"
          data-testid="top-nav-notifications-badge">
          <v-icon>mdi-bell-outline</v-icon>
        </v-badge>
      </v-btn>
    </template>
  </v-tooltip>
</template>

<style scoped>
/* The same sonar as the old check-in activator: a new arrival nobody has looked at yet */
.top-nav-notifications-unseen :deep(.v-badge__badge)::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background-color: inherit;
  animation: top-nav-notifications-sonar 1s infinite ease;
}

@keyframes top-nav-notifications-sonar {
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(2.5);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .top-nav-notifications-unseen :deep(.v-badge__badge)::after {
    animation: none;
  }
}
</style>
